import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    function handleScroll() {
      setScrolled(window.scrollY > 20);
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
      if (guideRef.current && !guideRef.current.contains(event.target as Node)) {
        setGuideOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
    setGuideOpen(false);
  }, [location]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="px-4 md:px-6 py-3 flex items-center justify-between">
        <Link
          to="/"
          className={`text-sm font-semibold tracking-wide transition-colors cursor-pointer whitespace-nowrap ${
            scrolled ? 'text-foreground-900' : 'text-foreground-800'
          }`}
        >
          pandabuy.cfd
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-2">
          <Link
            to="/"
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
              location.pathname === '/'
                ? 'bg-foreground-900 text-white'
                : scrolled
                ? 'text-foreground-700 hover:bg-background-100'
                : 'text-foreground-700 hover:bg-white/60'
            }`}
          >
            Home
          </Link>

          {/* Shopping Guide Dropdown */}
          <div ref={guideRef} className="relative">
            <button
              onClick={() => { setGuideOpen(!guideOpen); setDropdownOpen(false); }}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                location.pathname.startsWith('/article') || location.pathname === '/shopping-guide'
                  ? 'bg-foreground-900 text-white'
                  : scrolled
                  ? 'text-foreground-700 hover:bg-background-100'
                  : 'text-foreground-700 hover:bg-white/60'
              }`}
            >
              Shopping Guide
              <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${guideOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {guideOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg border border-background-200/60 py-2 max-h-96 overflow-y-auto">
                <Link
                  to="/shopping-guide"
                  className="block px-4 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer border-b border-background-200/40"
                >
                  View All Guides →
                </Link>
                {articles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="block px-4 py-2.5 text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {article.category}: {article.title.length > 40 ? article.title.slice(0, 40) + '...' : article.title}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Categories Dropdown */}
          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => { setDropdownOpen(!dropdownOpen); setGuideOpen(false); }}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                scrolled
                  ? 'border border-background-300 text-foreground-700 hover:bg-background-100'
                  : 'bg-white/70 backdrop-blur-sm border border-background-200/40 text-foreground-700 hover:bg-white'
              }`}
            >
              Browse Categories
              <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-background-200/60 py-2 max-h-80 overflow-y-auto">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.link}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
            scrolled ? 'text-foreground-900' : 'text-foreground-800'
          }`}
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {/* Mobile Menu Panel */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-background-200/60 shadow-lg max-h-[calc(100dvh-56px)] overflow-y-auto overscroll-contain">
          <div className="px-4 py-3 space-y-1">
            <Link
              to="/"
              className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                location.pathname === '/' ? 'bg-foreground-900 text-white' : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              Home
            </Link>

            <Link
              to="/shopping-guide"
              className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                location.pathname === '/shopping-guide' ? 'bg-foreground-900 text-white' : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              Shopping Guide
            </Link>

            <div className="pt-2">
              <p className="px-4 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">SEO Guides</p>
              {articles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="block px-4 py-2.5 text-sm text-foreground-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                >
                  {article.category}: {article.title.length > 35 ? article.title.slice(0, 35) + '...' : article.title}
                </Link>
              ))}
            </div>

            <div className="pt-2">
              <p className="px-4 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">Categories</p>
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.link}
                  target="_blank"
                  rel="noopener nofollow"
                  className="block px-4 py-2.5 text-sm text-foreground-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}